import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import moment from 'moment-timezone';
import { task, timeout } from 'ember-concurrency';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div class=\'current-time\' {{did-insert this.setupClock}}>\n  {{#if this.analogic}}\n    <svg\n      viewBox=\'0 0 200 200\'\n      aria-hidden=\'true\'\n      focusable=\'false\'\n      style=\'width: 100%; max-width: 200px; height: auto; background: var(--beebee-clock-bg, #41484F); border-radius: 22px;\'\n    >\n      <g>\n        <circle\n          id=\'circle\'\n          style=\'stroke: var(--beebee-clock-dial, #E6E7E9); stroke-width: 2px; fill: var(--beebee-clock-face, #FFFFFF)\'\n          cx=\'100\'\n          cy=\'100\'\n          r=\'80\'\n        ></circle>\n      </g>\n      <g>\n        <line\n          x1=\'100\'\n          y1=\'100\'\n          x2=\'100\'\n          y2=\'55\'\n          transform=\'rotate(397.619 100 100)\'\n          style=\'stroke-width: 3px; stroke: var(--beebee-clock-hand, #1B2A39);\'\n          id=\'hourhand\'\n        >\n          <animateTransform\n            attributeName=\'transform\'\n            attributeType=\'XML\'\n            type=\'rotate\'\n            dur=\'43200s\'\n            repeatCount=\'indefinite\'\n          />\n        </line>\n        <line\n          x1=\'100\'\n          y1=\'100\'\n          x2=\'100\'\n          y2=\'40\'\n          style=\'stroke-width: 4px; stroke: var(--beebee-clock-hand-minute, #919294);\'\n          id=\'minutehand\'\n          transform=\'rotate(451.432 100 100)\'\n        >\n          <animateTransform\n            attributeName=\'transform\'\n            attributeType=\'XML\'\n            type=\'rotate\'\n            dur=\'3600s\'\n            repeatCount=\'indefinite\'\n          />\n        </line>\n        <line\n          x1=\'100\'\n          y1=\'100\'\n          x2=\'100\'\n          y2=\'30\'\n          style=\'stroke-width: 2px; stroke: var(--beebee-clock-sweep, #DD425A);\'\n          id=\'secondhand\'\n          transform=\'rotate(199.928 100 100)\'\n        >\n          <animateTransform\n            attributeName=\'transform\'\n            attributeType=\'XML\'\n            type=\'rotate\'\n            dur=\'60s\'\n            repeatCount=\'indefinite\'\n          />\n        </line>\n      </g>\n      <circle\n        id=\'center\'\n        style=\'fill: var(--beebee-clock-pin, #546E7A); stroke: var(--beebee-clock-pin-ring, #FBFDFE); stroke-width: 2px;\'\n        cx=\'100\'\n        cy=\'100\'\n        r=\'3\'\n      ></circle>\n    </svg>\n  {{/if}}\n\n  {{#if this.digital}}\n    <div class=\'calendarDate\' role=\'timer\'>\n      <em>\n        {{this.dayName}}\n      </em>\n      <strong>\n        {{this.monthName}}\n      </strong>\n      <span>\n        {{this.dayNumber}}\n      </span>\n    </div>\n\n    <div class=\'calendarHour\' role=\'timer\'>\n      {{this.timeInWords}}\n    </div>\n  {{/if}}\n</div>");

var _dec, _class, _descriptor, _descriptor2;
let BbClockComponent = (_dec = task({
  restartable: true
}), _class = class BbClockComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "timeInWords", _descriptor, this);
    // _tick incrementato ogni secondo per forzare il ricalcolo dei getter data
    _initializerDefineProperty(this, "_tick", _descriptor2, this);
  }
  get digital() {
    return this.args.digital ?? false;
  }
  get analogic() {
    return this.args.analogic ?? true;
  }

  // Leggono _tick come dipendenza tracked: si ricalcolano ad ogni tick del timer
  get dayName() {
    void this._tick;
    return moment().format('dddd');
  }
  get monthName() {
    void this._tick;
    return moment().format('MMMM');
  }
  get dayNumber() {
    void this._tick;
    return moment().format('DD');
  }
  setupClock(element) {
    this._element = element;
    if (this.analogic) {
      this._initAnalogic(element);
    }

    // Il tickTask aggiorna sia l'ora digitale sia i getter data/giorno via _tick
    this.tickTask.perform();
  }
  *tickTask() {
    while (true) {
      const zone = this.args.time_zone || 'Europe/Rome';
      const format = this.args.format || 'HH:mm:ss';
      this.timeInWords = moment().tz(zone).format(format);
      this._tick++;
      yield timeout(1000);
    }
  }
  _initAnalogic(element) {
    const hands = [element.querySelector('#secondhand > *'), element.querySelector('#minutehand > *'), element.querySelector('#hourhand > *')];
    const cx = 100;
    const cy = 100;
    const date = new Date();
    const hoursAngle = 360 * date.getHours() / 12 + date.getMinutes() / 2;
    const minuteAngle = 360 * date.getMinutes() / 60;
    const secAngle = 360 * date.getSeconds() / 60;

    // Guard: le lancette potrebbero essere null se il DOM SVG non è ancora pronto
    if (hands[0]) {
      hands[0].setAttribute('from', this._shifter(secAngle, cx, cy));
      hands[0].setAttribute('to', this._shifter(secAngle + 360, cx, cy));
    }
    if (hands[1]) {
      hands[1].setAttribute('from', this._shifter(minuteAngle, cx, cy));
      hands[1].setAttribute('to', this._shifter(minuteAngle + 360, cx, cy));
    }
    if (hands[2]) {
      hands[2].setAttribute('from', this._shifter(hoursAngle, cx, cy));
      hands[2].setAttribute('to', this._shifter(hoursAngle + 360, cx, cy));
    }
    const svg = element.querySelector('svg');
    if (svg) {
      for (let i = 1; i <= 12; i++) {
        const el = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        el.setAttribute('x1', '100');
        el.setAttribute('y1', '30');
        el.setAttribute('x2', '100');
        el.setAttribute('y2', '40');
        el.setAttribute('transform', 'rotate(' + i * 360 / 12 + ' 100 100)');
        el.setAttribute('style', 'stroke: var(--beebee-clock-ticks, #919294);');
        svg.appendChild(el);
      }
    }
  }
  _shifter(val, cx, cy) {
    return [val, cx, cy].join(' ');
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this.tickTask.cancelAll();
    this._element = null;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "timeInWords", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_tick", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "setupClock", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setupClock"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "tickTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "tickTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbClockComponent);

export { BbClockComponent as default };
//# sourceMappingURL=bb-clock.js.map
